import * as THREE from 'three';
import { LECTERN, type Lesson } from '@vrlearning/lesson-schema';
import {
  LESSON_PREVIEW_TEXT,
  fullPanelTop,
  previewMarkerLayout,
} from '../bekijken/lesson-preview.js';
import { Button } from './panel.js';

/**
 * The marker of a lesson played as a preview (Steekproef, ADR-058 amendment): "Voorbeeld — telt niet
 * mee" as a label that does nothing, and Sluiten, in a group the caller hangs on the lectern's (or
 * the AR panel's), where previewMarkerLayout puts them. Sluiten is pressed like the lectern's own.
 * `panelTop` is the panel's top edge now (the VR panel is as tall as its words): the row follows it
 * on every frame it is drawn.
 */
export class PreviewMarker {
  readonly group = new THREE.Group();
  readonly pressable: Button[];
  readonly #buttons: Button[];
  #top: number;

  constructor(
    mode: Lesson['mode'],
    onClose: () => void,
    panelTop: () => number | undefined = () => undefined,
  ) {
    this.#top = fullPanelTop(mode);
    const place = previewMarkerLayout(mode);
    const label = new Button(LESSON_PREVIEW_TEXT.marker, () => undefined, {
      width: place.marker.width,
      height: place.marker.height,
      kind: 'muted',
    });
    label.mesh.position.set(place.marker.x, place.marker.y, 0.002);
    const close = new Button(LESSON_PREVIEW_TEXT.close, onClose, {
      width: place.close.width,
      height: place.close.height,
      kind: 'secondary',
    });
    close.mesh.position.set(place.close.x, place.close.y, 0.002);
    this.#buttons = [label, close];
    this.pressable = [close];
    this.group.add(label.mesh, close.mesh);
    this.group.name = 'preview-marker';
    // The whole row moves with the panel's top edge: both share one layout row.
    label.mesh.onBeforeRender = () => {
      const top = panelTop();
      if (top === undefined || top === this.#top) return;
      this.#top = top;
      this.group.position.y = top - fullPanelTop(mode);
    };
  }

  dispose(): void {
    for (const button of this.#buttons) button.dispose();
    this.group.removeFromParent();
  }
}

/**
 * The VR lectern panel's top edge now, found in the lectern's group: its first plane as wide as the
 * lectern (the panel is added first; render/ui.ts), as tall as its words. Undefined in AR, whose panel
 * keeps its size.
 */
export function lecternPanelTop(group: THREE.Object3D): (() => number) | undefined {
  const panel = group.children.find(
    (child): child is THREE.Mesh<THREE.PlaneGeometry> =>
      child instanceof THREE.Mesh &&
      child.geometry instanceof THREE.PlaneGeometry &&
      child.geometry.parameters.width === LECTERN.width,
  );
  return panel ? () => panel.position.y + panel.geometry.parameters.height / 2 : undefined;
}
